import { zodResolver } from '@hookform/resolvers/zod';
import { Search, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { Controller, useForm } from 'react-hook-form';
import type { Account } from '@/api/commercial/accounts';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { DateRangeInput } from '@/components/ui/date-range-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useConfigurationProfilesClients,
  useConfigurationProfilesTechnologies,
} from '@/hooks/production/use-configuration-profiles';
import {
  type ConfigurationProfilesFiltersFormData,
  configurationProfilesFiltersSchema,
} from '@/schemas/production/configuration-profiles-filters.schema';
import { CONFIGURATION_PROFILE_FILTER_STATUS_OPTIONS } from '@/types/production/configuration-profile-list.type';

interface ConfigurationProfilesFiltersProps {
  onApplyFilters: (filters: ConfigurationProfilesFiltersFormData) => void;
  onClearFilters: () => void;
}

export function ConfigurationProfilesFilters({
  onApplyFilters,
  onClearFilters,
}: ConfigurationProfilesFiltersProps) {
  const [clientSearch, setClientSearch] = useState('');
  const { technologies } = useConfigurationProfilesTechnologies();
  const { clients, isLoading: isLoadingClients } = useConfigurationProfilesClients(
    clientSearch || undefined,
  );

  const form = useForm<ConfigurationProfilesFiltersFormData>({
    resolver: zodResolver(configurationProfilesFiltersSchema),
    defaultValues: {
      name: '',
      clientId: '',
      status: 'all',
      technologyId: '',
      createdAtRange: undefined,
      editedAtRange: undefined,
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    onApplyFilters(data);
  });

  const handleClear = useCallback(() => {
    form.reset({
      name: '',
      clientId: '',
      status: 'all',
      technologyId: '',
      createdAtRange: undefined,
      editedAtRange: undefined,
    });
    setClientSearch('');
    onClearFilters();
  }, [form, onClearFilters]);

  const handleClientSearch = useCallback((query: string) => {
    setClientSearch(query);
  }, []);

  const handleClientChange = useCallback(
    (selected: Account[]) => {
      const clientId = selected.length > 0 ? selected[0].id : '';
      form.setValue('clientId', clientId);
    },
    [form],
  );

  const selectedClientId = form.watch('clientId');
  const selectedClient = selectedClientId
    ? (clients.find((c) => c.id === selectedClientId) ?? (undefined as Account | undefined))
    : undefined;

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="filter-name">Nome</Label>
          <Input id="filter-name" placeholder="Buscar por nome..." {...form.register('name')} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filter-client">Cliente</Label>
          <Controller
            control={form.control}
            name="clientId"
            render={() => (
              <Combobox<Account>
                id="filter-client"
                data={clients}
                keyExtractor={(account) => account.id}
                displayValueGetter={(account) =>
                  account.fantasy_name ??
                  account.name ??
                  account.social_name ??
                  account.document?.value ??
                  ''
                }
                type="single"
                value={selectedClient ? [selectedClient] : []}
                onOptionChange={handleClientChange}
                onSearchChange={handleClientSearch}
                placeholder="Selecionar cliente..."
                isLoading={isLoadingClients}
                disableLocalFilter
              />
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filter-status">Status</Label>
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select value={field.value ?? 'all'} onValueChange={field.onChange}>
                <SelectTrigger id="filter-status">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {CONFIGURATION_PROFILE_FILTER_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filter-technology">Tecnologia</Label>
          <Controller
            control={form.control}
            name="technologyId"
            render={({ field }) => (
              <Select value={field.value ?? ''} onValueChange={field.onChange}>
                <SelectTrigger id="filter-technology">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {technologies.map((tech) => (
                    <SelectItem key={tech.id} value={tech.id}>
                      {tech.name.brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-1.5">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: DateRangeInput doesn't accept id */}
          <Label>Data de Cadastro</Label>
          <Controller
            control={form.control}
            name="createdAtRange"
            render={({ field }) => (
              <DateRangeInput
                value={field.value as DateRange | undefined}
                onChange={field.onChange}
                showTimeInputs={false}
              />
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Data de Edição/Validação</Label>
          <Controller
            control={form.control}
            name="editedAtRange"
            render={({ field }) => (
              <DateRangeInput
                value={field.value as DateRange | undefined}
                onChange={field.onChange}
                showTimeInputs={false}
              />
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" className="bg-black hover:bg-gray-800 text-white">
          <Search className="mr-2 h-4 w-4" />
          Pesquisar
        </Button>
        <Button type="button" variant="outline" onClick={handleClear}>
          <X className="mr-2 h-4 w-4" />
          Limpar
        </Button>
      </div>
    </form>
  );
}
